import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listGuides } from "../services/guides";
import { GUIDE_CATEGORIES, GUIDE_STATUSES } from "../types/guide";
import type { CharacterProfile } from "../types/guide";
import { StatusBadge } from "../components/StatusBadge";

type SortKey = "updatedAt" | "name" | "status" | "sortOrder";
type EnabledFilter = "all" | "enabled" | "disabled";

const PROVIDERS = ["gemini", "deepgram", "elevenlabs", "gcp"];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function GuideListPage() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState<CharacterProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [category, setCategory] = useState<string>("all");
  const [provider, setProvider] = useState<string>("all");
  const [enabled, setEnabled] = useState<EnabledFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setGuides(await listGuides());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load companions");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = guides.filter((g) => {
      if (status !== "all" && g.status !== status) return false;
      if (category !== "all" && g.category !== category) return false;
      if (provider !== "all" && g.tts.provider !== provider) return false;
      if (enabled === "enabled" && g.enabled === false) return false;
      if (enabled === "disabled" && g.enabled !== false) return false;
      if (q) {
        const hay = `${g.id} ${g.name} ${g.role} ${g.tagline}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "name":
          cmp = (a.name || a.id).localeCompare(b.name || b.id);
          break;
        case "status":
          cmp = a.status.localeCompare(b.status);
          break;
        case "sortOrder":
          cmp = a.sortOrder - b.sortOrder;
          break;
        default:
          cmp = a.updatedAt.localeCompare(b.updatedAt);
      }
      return cmp * dir;
    });
    return rows;
  }, [guides, search, status, category, provider, enabled, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  return (
    <div className="list-page">
      <div className="list-toolbar">
        <h1>Companions</h1>
        <input
          className="search-box"
          type="search"
          placeholder="Search id, name, role, tagline…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="spacer" />
        <button className="btn" onClick={load}>↻ Refresh</button>
        <button className="btn btn-primary" onClick={() => navigate("/guides/new")}>+ New guide</button>
      </div>

      <div className="filters">
        <div className="filter">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {GUIDE_STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace("_", " ")}</option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label>Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">All categories</option>
            {GUIDE_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.replace("_", " ")}</option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label>Voice provider</label>
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="all">All providers</option>
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label>Enabled</label>
          <select value={enabled} onChange={(e) => setEnabled(e.target.value as EnabledFilter)}>
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="state-box">Loading companions…</div>
      ) : error ? (
        <div className="state-box error">{error}</div>
      ) : (
        <>
          <div className="result-count">
            {filtered.length} of {guides.length} companion{guides.length === 1 ? "" : "s"}
          </div>
          {filtered.length === 0 ? (
            <div className="state-box">No companions match your filters.</div>
          ) : (
            <table className="guide-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort("name")}>Companion{arrow("name")}</th>
                  <th onClick={() => toggleSort("status")}>Status{arrow("status")}</th>
                  <th className="no-sort">Category</th>
                  <th className="no-sort">Voice</th>
                  <th className="no-sort">Ver</th>
                  <th onClick={() => toggleSort("updatedAt")}>Updated{arrow("updatedAt")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g) => (
                  <tr key={g.id} className="guide-row" onClick={() => navigate(`/guides/${g.id}`)}>
                    <td>
                      <div className="guide-name">
                        <span className="accent-dot" style={g.accentColor ? { background: g.accentColor } : undefined} />
                        <div>
                          <div className="name">{g.name || g.id}</div>
                          <div className="role">{g.role || g.id}</div>
                        </div>
                      </div>
                    </td>
                    <td><StatusBadge status={g.status} enabled={g.enabled} /></td>
                    <td className="voice-cell">{g.category.replace("_", " ")}</td>
                    <td className="voice-cell mono">{g.tts.provider} · {g.tts.voiceId}</td>
                    <td className="voice-cell">v{g.version}</td>
                    <td className="voice-cell">{formatDate(g.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
